/**
 * 描画モデル（フレームワーク非依存の純関数）— Phase 2 / Slice 1
 * -----------------------------------------------------------------------------
 * reference/core/render.mjs の「描画ロジック」を Preact 移植のために純関数へ抽出したもの。
 * ここには DOM/JSX を持たない（= node で単体テストできる）。Preact 側（Block.tsx 等）は
 * この関数群を使って要素を組み立てるだけにする。トークン/レイアウト規則は render.mjs と一致。
 *
 * 参照実装との対応:
 *  - DEMO_TOKENS / styleCss / layoutCss / label → 同名ロジックを移植
 *  - groupRows / groupAggLabel / chartModel / tableSpecs / primaryColId → 同ロジック
 *  - visibility の評価は reference/core/condition.mjs をそのまま流用（SSOT・複製しない）
 */
import { evaluateCondition } from "../../../reference/core/condition.mjs";

/** SheetSnap 既定トークン（Ray of Ray ブランド＝ライト:ネイビー×ミント / D-0052）。app.brand.tokens で上書き可能。
 *  トークン=デザインシステムの唯一の可変点。ここを増やす＝ビルダーで制御できる見た目が増える。 */
export const DEMO_TOKENS = {
  // 面（背景）
  "color.bg.app": "#fafaf8", // ページ地（canvas-soft）
  "color.bg.card": "#ffffff",
  "color.bg.accent": "#1b1938",
  // 文字
  "color.fg.default": "#292827", // ink
  "color.fg.inverse": "#ffffff",
  "color.fg.muted": "#73706d",   // ink-mute
  "color.fg.onBrand": "#ffffff", // ブランド面（ボタン/ナビ/FAB）の文字色
  // ブランド（インディゴ主色＋ミントアクセント／rayofray-site）
  "color.brand.primary": "#1b1938",
  "color.brand.mint": "#8ec9b3",
  // 線・影・標高（②）
  "color.border": "#e8e4dd", // hairline
  "shadow.sm": "0 1px 2px rgba(27,25,56,0.07)",
  "shadow.md": "0 6px 20px rgba(27,25,56,0.12)",
  // セマンティックなステータス色（①）— 値ペア（bg / fg）。EnumOption.colorToken が名前で参照。
  "color.chip.neutral.bg": "#eeece6", "color.chip.neutral.fg": "#73706d",
  "color.chip.info.bg": "#ece9f9", "color.chip.info.fg": "#3f3a52",
  "color.chip.progress.bg": "#e0efe8", "color.chip.progress.fg": "#3f7d68",
  "color.chip.success.bg": "#e6f6ec", "color.chip.success.fg": "#1a7f37",
  "color.chip.warning.bg": "#fbf0d8", "color.chip.warning.fg": "#8a6412",
  "color.chip.danger.bg": "#fbe7e4", "color.chip.danger.fg": "#b5433a",
  // 間隔・角丸・文字サイズ
  "space.1": "4px",
  "space.2": "8px",
  "space.3": "12px",
  "space.4": "16px",
  "radius.md": "10px",
  "radius.lg": "14px",
  // callAI と同じ文字スケール（body13 / カード名12 / メタ10.5・D-0058）
  "font.sm": "10.5px",
  "font.md": "12px",
  "font.lg": "13px",
  // 現在のスキーム（③ enumChip の自動パレット選択に使う内部トークン）
  "color.scheme": "light",
};

/** ダーク・テーマの上書き（③・rayofray-site のダーク＝ディープインディゴ×ラベンダー/ミント / D-0053）。 */
export const DARK_TOKENS = {
  "color.scheme": "dark",
  "color.bg.app": "#0e0c1f",   // primary-deep
  "color.bg.card": "#1a1830",
  "color.fg.default": "#f2f1f7",
  "color.fg.inverse": "#0e0c1f",
  "color.fg.muted": "#bcbac9", // on-dark-mute
  "color.fg.onBrand": "#1b1938", // ラベンダー地には濃字
  // ダークはラベンダー（violet-soft）が主役・ミントがアクセント
  "color.brand.primary": "#c9b4fa",
  "color.brand.mint": "#8ec9b3",
  "color.bg.accent": "#c9b4fa",
  "color.border": "rgba(201,180,250,0.20)",
  "shadow.sm": "0 1px 2px rgba(0,0,0,0.45)",
  "shadow.md": "0 6px 22px rgba(0,0,0,0.55)",
  // ステータス色（暗地向けに沈めた背景＋明るい前景）
  "color.chip.neutral.bg": "#2a2740", "color.chip.neutral.fg": "#bcbac9",
  "color.chip.info.bg": "#2a2145", "color.chip.info.fg": "#c9b4fa",
  "color.chip.progress.bg": "#12352c", "color.chip.progress.fg": "#8ec9b3",
  "color.chip.success.bg": "#16351f", "color.chip.success.fg": "#86e0a0",
  "color.chip.warning.bg": "#3a2e12", "color.chip.warning.fg": "#e8c069",
  "color.chip.danger.bg": "#3e1d24", "color.chip.danger.fg": "#f0a0b0",
};

/** ランタイムのシェル CSS が参照する CSS カスタムプロパティ文字列を組む。
 *  トークン→変数化の核。app.brand.tokens 上書きが .ss-app に流れ、シェルの見た目も差し替わる。 */
export function cssVars(tokens) {
  const v = (name, tokenKey, fallback) => `--${name}:${tokens[tokenKey] ?? fallback}`;
  return [
    v("brand", "color.brand.primary", "#1b1938"),
    v("on-brand", "color.fg.onBrand", "#ffffff"),
    v("fg", "color.fg.default", "#292827"),
    v("muted", "color.fg.muted", "#73706d"),
    v("bg", "color.bg.app", "#fafaf8"),
    v("card", "color.bg.card", "#ffffff"),
    v("border-c", "color.border", "#e8e4dd"),
    v("shadow-sm", "shadow.sm", "0 1px 2px rgba(27,25,56,0.07)"),
    v("shadow-md", "shadow.md", "0 6px 20px rgba(27,25,56,0.12)"),
    v("radius-md", "radius.md", "10px"),
    v("radius-lg", "radius.lg", "14px"),
  ].join(";");
}

export const MAX_DEPTH = 3; // container 入れ子上限（RISK B-5）

const tok = (tokens, t) => (t != null ? tokens[t] : undefined);

/** enum チップの自動パレット（SheetSnap サンセット調・低彩度の背景＋可読な前景）。
 *  options 内の位置で安定割り当て（順序が変わらない限り同じステータス＝同じ色）。 */
// 自動パレット（enum に colorToken 指定が無いとき）。rayofray 調和・先頭はインディゴ/ミント（オレンジ非先頭）。
export const CHIP_PALETTE = [
  { bg: "#ece9f9", fg: "#3f3a52" }, // indigo（新規等の既定）
  { bg: "#e0efe8", fg: "#3f7d68" }, // mint
  { bg: "#efe7fb", fg: "#6b4fa0" }, // violet
  { bg: "#fbf0d8", fg: "#8a6412" }, // amber
  { bg: "#fbe7e4", fg: "#b5433a" }, // rose
  { bg: "#dcecec", fg: "#155555" }, // teal
  { bg: "#e6f6ec", fg: "#1a7f37" }, // green
  { bg: "#eeece6", fg: "#73706d" }, // warm gray
];

/** ダーク時の自動パレット（③・沈めた背景＋明るい前景）。CHIP_PALETTE と同順。 */
export const DARK_CHIP_PALETTE = [
  { bg: "#2a2145", fg: "#c9b4fa" }, // indigo/violet
  { bg: "#12352c", fg: "#8ec9b3" }, // mint
  { bg: "#2c2150", fg: "#c4a6f0" }, // violet
  { bg: "#3a2e12", fg: "#e8c069" }, // amber
  { bg: "#3e1d24", fg: "#f0a0b0" }, // rose
  { bg: "#123c3c", fg: "#7fcece" }, // teal
  { bg: "#16351f", fg: "#86e0a0" }, // green
  { bg: "#2a2740", fg: "#bcbac9" }, // warm gray
];

/** 背景色に対して読みやすい前景（明るい背景=濃字／暗い背景=白字）。 */
function readableFg(hex) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex ?? "");
  if (!m) return "#ffffff";
  const n = parseInt(m[1], 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? "#2A2422" : "#ffffff";
}

// ---- 主色からの配色導出（Theme Studio「メインカラーで一括」）----------------
function hexToRgb(hex) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex ?? "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex(r, g, b) {
  const h = (x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}
function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h * 360, s, l];
}
function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360 / 360;
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const hue = (p, q, t) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue(p, q, h + 1 / 3); g = hue(p, q, h); b = hue(p, q, h - 1 / 3);
  }
  return rgbToHex(r * 255, g * 255, b * 255);
}

/** 主色（ブランド色）1つから調和した配色トークンを導出する（純関数）。
 *  安全のため light/dark 両モードで破綻しない「彩度のあるブランド系」だけを返す
 *  （地色/文字/境界はモード側 DARK_TOKENS に任せ、上書きしない＝ダーク時の白飛びを避ける）。 */
export function derivePalette(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return { "color.brand.primary": hex };
  const [h, s, l] = rgbToHsl(...rgb);
  // アクセント = 色相を +26° 回し、やや明るく（同系の相棒色。彩度は下限を確保）。
  const accent = hslToHex(h + 26, Math.max(0.5, Math.min(1, s)), Math.min(0.64, l + 0.1));
  return {
    "color.brand.primary": hex,
    "color.bg.accent": hex,
    "color.brand.mint": accent,
  };
}

/** enum 列の値 → 表示チップ { text, bg, fg }。enum でない/空なら null（呼び出し側はプレーン表示）。
 *  色の解決順（①セマンティック対応）:
 *   1. colorToken がセマンティック名（neutral/info/progress/success/warning/danger）→ color.chip.<name>.bg/fg ペア
 *   2. colorToken が生のトークンキー → その色を bg・輝度から fg を決定
 *   3. 指定なし → options 内の位置で CHIP_PALETTE を安定割り当て
 *  text は option のローカライズ済みラベル（無ければ生値）。 */
export function enumChip(col, value, tokens, locale) {
  if (!col || col.type !== "enum" || value == null || value === "") return null;
  const opts = col.options ?? [];
  const idx = opts.findIndex((o) => o.value === value);
  const opt = idx >= 0 ? opts[idx] : null;
  const text = opt ? label(opt.label, locale) || opt.value : String(value);
  const ct = opt?.colorToken;
  if (ct) {
    // 1. セマンティック名（color.chip.<name>.bg/fg のペアが定義済みか）
    const bg = tokens?.[`color.chip.${ct}.bg`];
    const fg = tokens?.[`color.chip.${ct}.fg`];
    if (bg && fg) return { text, bg, fg };
    // 2. 生のトークンキー（例: color.brand.primary）
    const raw = tok(tokens, ct);
    if (raw) return { text, bg: raw, fg: readableFg(raw) };
  }
  // 3. 位置ベースの自動パレット（スキームに応じて light/dark）
  const palette = tokens?.["color.scheme"] === "dark" ? DARK_CHIP_PALETTE : CHIP_PALETTE;
  const pal = palette[(idx >= 0 ? idx : 0) % palette.length];
  return { text, bg: pal.bg, fg: pal.fg };
}

/** ベーストークン（mode で light/dark）< アプリのブランド上書き（テーマ編集 / D-0020・③）。 */
export function resolveTokens(app, mode = "light", base = DEMO_TOKENS) {
  const themed = mode === "dark" ? { ...base, ...DARK_TOKENS } : base;
  return { ...themed, ...(app?.brand?.tokens ?? {}) };
}

/** 実効スキームの決定（純関数・③）。ユーザーの明示トグルが最優先、次に app の base、auto はシステム設定。 */
export function resolveMode(appBase, systemDark, override) {
  if (override === "light" || override === "dark") return override;
  if (appBase === "light" || appBase === "dark") return appBase;
  return systemDark ? "dark" : "light"; // auto / 未指定
}

/** style（トークン参照のみ）→ CSS 文字列。未知トークンは無視（事故らない）。 */
export function styleCss(style, tokens) {
  if (!style) return "";
  const css = [];
  const bg = tok(tokens, style.bg); if (bg) css.push(`background:${bg}`);
  const fg = tok(tokens, style.fg); if (fg) css.push(`color:${fg}`);
  const pad = tok(tokens, style.padding); if (pad) css.push(`padding:${pad}`);
  const rad = tok(tokens, style.radius); if (rad) css.push(`border-radius:${rad}`);
  const fs = tok(tokens, style.fontSize); if (fs) css.push(`font-size:${fs}`);
  if (style.align) css.push(`text-align:${{ start: "left", center: "center", end: "right" }[style.align]}`);
  if (style.fontWeight) css.push(`font-weight:${{ regular: 400, medium: 500, bold: 700 }[style.fontWeight]}`);
  return css.join(";");
}

/** ブロックの grid 配置 CSS（不正位置は null=描画スキップ / RISK B-5）。 */
export function blockGridCss(position, gridColumns) {
  const p = position;
  if (!p || p.colStart < 1 || p.colSpan < 1 || p.colStart + p.colSpan - 1 > gridColumns) return null;
  const css = [`grid-column:${p.colStart} / span ${p.colSpan}`];
  if (p.rowStart != null) css.push(`grid-row:${p.rowStart}${p.rowSpan ? ` / span ${p.rowSpan}` : ""}`);
  return css.join(";");
}

/** layout.grid / breakpoints → スコープ付き CSS（1 定義でレスポンシブ）。 */
export function layoutCss(viewId, layout, tokens) {
  const gap = tok(tokens, layout.grid.gap) ?? "8px";
  let css = `[data-view="${viewId}"] .grid.card{display:grid;grid-template-columns:repeat(${layout.grid.columns},1fr);gap:${gap};}`;
  css += `[data-view="${viewId}"] .grid.sub{display:grid;gap:${gap};}`;
  for (const bp of layout.breakpoints ?? []) {
    css += `@media (max-width:${bp.maxWidth}px){[data-view="${viewId}"] .grid.card{grid-template-columns:repeat(${bp.columns},1fr);}}`;
  }
  return css;
}

/** ja/en 表示文字列の解決。 */
export function label(lt, locale) {
  if (lt == null) return "";
  return locale === "en" && lt.en ? lt.en : lt.ja;
}

/** visibility 条件の評価（reference の condition.mjs を流用）。条件が無ければ true。 */
export function isVisible(condition, row, evalCtx) {
  return evaluateCondition(condition, row ?? {}, evalCtx);
}

/** 列でグルーピング（null 列なら全体 1 グループ）。Map<string, rows[]>。 */
export function groupRows(rows, col) {
  const groups = new Map();
  for (const r of rows) {
    const k = col ? String(r[col] ?? "（なし）") : "（すべて）";
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  }
  return groups;
}

/** グループ集計ラベル（count/sum）。 */
export function groupAggLabel(agg, rows) {
  if (!agg || agg.agg === "count") return `${rows.length} 件`;
  if (agg.agg === "sum") {
    const s = rows.reduce((n, r) => n + (Number(r[agg.columnId]) || 0), 0);
    return `合計 ${s}`;
  }
  return `${rows.length} 件`;
}

/** 棒グラフのモデル（集計）。Chart コンポーネントが描く。 */
export function chartModel(spec, rows) {
  const s = spec || { kind: "bar" };
  const groups = new Map();
  for (const r of rows) {
    const k = s.groupByColumnId ? String(r[s.groupByColumnId] ?? "(空)") : "(全体)";
    const add = !s.agg || s.agg === "count" ? 1 : (Number(r[s.valueColumnId]) || 0);
    groups.set(k, (groups.get(k) || 0) + add);
  }
  const entries = [...groups.entries()];
  const max = Math.max(1, ...entries.map((e) => e[1]));
  return { bars: entries.map(([k, v]) => ({ k, v, w: Math.round((v / max) * 100) })), max };
}

/** table ビューの列仕様（順序・幅）。未指定なら全列。存在しない列は落とす。 */
export function tableSpecs(view, table) {
  const cols = table?.columns ?? [];
  return view.tableColumns?.length
    ? view.tableColumns.filter((tc) => cols.some((c) => c.id === tc.columnId))
    : cols.map((c) => ({ columnId: c.id }));
}

export const widthOf = (w) => (w === "narrow" ? "72px" : w === "wide" ? "220px" : "130px");

/** 書き戻しキー（列の sourceRef 優先）。 */
export const keyOf = (col, columnId) => col?.sourceRef ?? columnId ?? "";

/** 行の代表テキスト列（calendar/map/board のチップ表示用）= 最初の field ブロック or 主キー。 */
export function primaryColId(view, table) {
  return (
    view.layout.blocks.find((b) => b.component === "field" && b.binding)?.binding?.columnId ??
    table?.primaryKey
  );
}

/** calendar ビューの日付列（未指定なら最初の date/datetime 列）。 */
export function calendarDateCol(view, table) {
  return view.dateColumnId ?? table?.columns.find((c) => c.type === "date" || c.type === "datetime")?.id;
}

/** map ビューの位置列（未指定なら location 型/住所らしい列）。 */
export function mapLocationCol(view, table) {
  return (
    view.locationColumnId ??
    table?.columns.find((c) => c.type === "location" || /住所|所在地|エリア|地域|address|location/i.test(c.id))?.id
  );
}

/** 月グリッドの日付→行 割り当て（calendar）。now を渡せるので純関数・テスト可能。 */
export function calendarModel(rows, dateCol, now = new Date()) {
  const year = now.getFullYear();
  const month = now.getMonth();
  const byDay = new Map();
  if (dateCol) {
    for (const r of rows) {
      const d = r[dateCol] ? new Date(r[dateCol]) : null;
      if (d && !isNaN(d.getTime()) && d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!byDay.has(day)) byDay.set(day, []);
        byDay.get(day).push(r);
      }
    }
  }
  return {
    year,
    month,
    firstDow: new Date(year, month, 1).getDay(),
    daysIn: new Date(year, month + 1, 0).getDate(),
    byDay,
  };
}
